import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import { Button, TextField, TextareaField } from '@dopomogai/ui';

import {
  WEBSITE_V2_WIDGET_LAUNCHER,
  WEBSITE_V2_WIDGET_TITLE,
  websiteV2WidgetQuickReplies,
  websiteV2WidgetRouteSummaries,
} from '../content';
import type { ReferralAttribution } from '../referral';
import { persistWidgetHandoff } from '../storage';
import { useWebsiteV2Analytics, type WebsiteV2PageType } from '../analytics';

type WidgetRoute = keyof typeof websiteV2WidgetRouteSummaries;

type WidgetHandoffForm = {
  name: string;
  email: string;
  company: string;
  role: string;
  website: string;
  urgency: string;
  decisionMakerStatus: string;
  painArea: string;
};

const initialForm: WidgetHandoffForm = {
  name: '',
  email: '',
  company: '',
  role: '',
  website: '',
  urgency: '',
  decisionMakerStatus: '',
  painArea: '',
};

const resolveRouteFromReply = (reply: string): WidgetRoute => {
  if (reply === 'Our website is underperforming' || reply === 'We need better lead qualification') {
    return 'website_sales_concierge_discussion';
  }
  if (reply === 'I’m not sure where to start') {
    return 'short_qualification_call';
  }
  return 'ai_revenue_audit';
};

type Props = {
  referral: ReferralAttribution;
};

export function SalesConciergeWidget({ referral }: Props) {
  const location = useLocation();
  const pageType: WebsiteV2PageType =
    location.pathname === '/ai-revenue-audit'
      ? 'ai_revenue_audit'
      : location.pathname === '/thank-you'
        ? 'thank_you'
        : 'software_services';
  const { track } = useWebsiteV2Analytics(pageType, { trackPageView: false });
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReply, setSelectedReply] = useState<string | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<WidgetRoute | null>(null);
  const [showHandoffForm, setShowHandoffForm] = useState(false);
  const [handoffForm, setHandoffForm] = useState<WidgetHandoffForm>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) track('widget_opened', { launcher_label: WEBSITE_V2_WIDGET_LAUNCHER, widget_name: WEBSITE_V2_WIDGET_TITLE });
  }, [isOpen, track]);

  const selectedSummary = selectedRoute ? websiteV2WidgetRouteSummaries[selectedRoute] : null;

  const routeCtaText = useMemo(() => {
    if (!selectedSummary) return '';
    return selectedSummary.cta;
  }, [selectedSummary]);

  const resetWidget = () => {
    setSelectedReply(null);
    setSelectedRoute(null);
    setShowHandoffForm(false);
    setHandoffForm(initialForm);
    setIsSubmitting(false);
    setSubmitError(null);
    setIsSuccess(false);
  };

  const handleOpenChange = () => {
    setIsOpen((current) => {
      const next = !current;
      if (!next) resetWidget();
      return next;
    });
  };

  const handleReply = (reply: string) => {
    const route = resolveRouteFromReply(reply);
    setSelectedReply(reply);
    setSelectedRoute(route);
    track('widget_quick_reply_clicked', {
      widget_name: WEBSITE_V2_WIDGET_TITLE,
      quick_reply_label: reply,
    });
    track('widget_route_selected', {
      widget_name: WEBSITE_V2_WIDGET_TITLE,
      selected_route: route,
      route_basis: websiteV2WidgetRouteSummaries[route].routeBasis,
    });
  };

  const handleRouteAction = () => {
    if (!selectedRoute) return;

    track('widget_handoff_started', {
      widget_name: WEBSITE_V2_WIDGET_TITLE,
      selected_route: selectedRoute,
      handoff_type: selectedRoute === 'ai_revenue_audit' ? 'audit_page' : selectedRoute === 'short_qualification_call' ? 'qualification_call' : 'short_form',
    });

    if (selectedRoute === 'ai_revenue_audit') {
      const nextUrl = referral.isReferral
        ? `/ai-revenue-audit?${new URLSearchParams({
            partner_id: referral.partnerId || '',
            partner: referral.partnerRaw || '',
            ref: referral.ref || '',
            referral_code: referral.referralCode || '',
            entry_offer: referral.entryOffer,
            utm_source: referral.utm.source || '',
            utm_medium: referral.utm.medium || '',
            utm_campaign: referral.utm.campaign || '',
            utm_content: referral.utm.content || '',
            utm_term: referral.utm.term || '',
          }).toString()}`
        : '/ai-revenue-audit';
      window.location.assign(nextUrl);
      return;
    }

    setShowHandoffForm(true);
  };

  const handleHandoffSubmit = async () => {
    if (!selectedRoute) return;
    if (!handoffForm.name || !handoffForm.email || !handoffForm.company) {
      setSubmitError('Please add your name, work email, and company so we can follow up.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await persistWidgetHandoff({
        selected_route: selectedRoute,
        quick_reply: selectedReply,
        pain_area: handoffForm.painArea || selectedReply,
        name: handoffForm.name,
        email: handoffForm.email,
        company: handoffForm.company,
        role: handoffForm.role,
        website: handoffForm.website,
        urgency: handoffForm.urgency,
        decision_maker_status: handoffForm.decisionMakerStatus,
        is_referral: referral.isReferral,
        referral_partner_id: referral.partnerId,
        partner_name_raw: referral.partnerRaw,
        referral_code: referral.referralCode,
        entry_offer: referral.entryOffer,
        landing_page: referral.landingPage,
        utm_source: referral.utm.source,
        utm_medium: referral.utm.medium,
        utm_campaign: referral.utm.campaign,
        utm_content: referral.utm.content,
        utm_term: referral.utm.term,
        session_id: referral.sessionId,
      });
      setIsSuccess(true);
    } catch {
      setSubmitError('Something went wrong. Please retry or continue to the audit route instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpenChange}
        className="fixed bottom-5 right-4 z-50 inline-flex items-center gap-3 rounded-full border-2 border-black bg-slate-950 px-4 py-3 text-sm font-black text-white shadow-[6px_6px_0px_0px_rgba(15,23,42,1)] transition-transform hover:-translate-x-1 hover:-translate-y-1 md:bottom-8 md:right-8"
      >
        <MessageSquare size={18} />
        <span>{WEBSITE_V2_WIDGET_LAUNCHER}</span>
      </button>

      {isOpen ? (
        <div className="fixed bottom-24 right-4 z-50 w-[min(92vw,24rem)] rounded-[1.75rem] border-2 border-black bg-white shadow-[10px_10px_0px_0px_rgba(15,23,42,1)] md:bottom-28 md:right-8">
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-slate-900">{WEBSITE_V2_WIDGET_TITLE}</p>
              <p className="mt-1 text-xs text-slate-500">Routes you to the right next step.</p>
            </div>
            <button type="button" onClick={handleOpenChange} className="rounded-full border border-slate-200 p-2 text-slate-500">
              <X size={16} />
            </button>
          </div>

          <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-5">
            {!selectedRoute && !showHandoffForm && !isSuccess ? (
              <>
                <div className="rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                  {referral.isReferral
                    ? 'You were introduced to Dopomogai — want help figuring out whether the AI Revenue Audit or a narrower next step makes more sense?'
                    : 'I can help you find the right next step — AI Revenue Audit, Website Sales Concierge setup, or a short qualification call.'}
                </div>
                <div className="grid gap-2">
                  {websiteV2WidgetQuickReplies.map((reply) => (
                    <button
                      key={reply}
                      type="button"
                      onClick={() => handleReply(reply)}
                      className="rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition-colors hover:border-emerald-500 hover:bg-emerald-50"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </>
            ) : null}

            {selectedSummary && !showHandoffForm && !isSuccess ? (
              <>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                  {selectedSummary.summary}
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button text={routeCtaText} onClick={handleRouteAction} />
                  <Button variant="ghost" text="Start over" onClick={resetWidget} />
                </div>
              </>
            ) : null}

            {showHandoffForm && !isSuccess ? (
              <div className="space-y-4">
                <TextField
                  label="Name"
                  value={handoffForm.name}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, name: event.target.value }))}
                />
                <TextField
                  label="Work email"
                  type="email"
                  value={handoffForm.email}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, email: event.target.value }))}
                />
                <TextField
                  label="Company"
                  value={handoffForm.company}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, company: event.target.value }))}
                />
                <TextField
                  label="Role"
                  value={handoffForm.role}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, role: event.target.value }))}
                />
                <TextField
                  label="Website"
                  value={handoffForm.website}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, website: event.target.value }))}
                />
                <TextField
                  label="Urgency"
                  value={handoffForm.urgency}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, urgency: event.target.value }))}
                  placeholder="Immediately, within 30 days, later..."
                />
                <TextField
                  label="Decision-maker status"
                  value={handoffForm.decisionMakerStatus}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, decisionMakerStatus: event.target.value }))}
                  placeholder="Decision-maker, sponsor, operator..."
                />
                <TextareaField
                  label="Main pain"
                  rows={4}
                  value={handoffForm.painArea}
                  onChange={(event) => setHandoffForm((current) => ({ ...current, painArea: event.target.value }))}
                />
                {submitError ? (
                  <p className="text-sm font-medium text-rose-600">{submitError}</p>
                ) : null}
                <div className="flex flex-wrap gap-3">
                  <Button text="Send context for manual review" loading={isSubmitting} onClick={handleHandoffSubmit} />
                  <Button variant="ghost" text="Back" onClick={() => setShowHandoffForm(false)} />
                </div>
              </div>
            ) : null}

            {isSuccess ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">
                  Thanks. We have enough context to route this manually and confirm the best next step.
                </div>
                <p className="text-sm leading-6 text-slate-600">
                  Expect a follow-up after manual review. If you want to keep momentum, you can also continue directly to the audit application.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button text="Continue to the Audit" onClick={() => window.location.assign('/ai-revenue-audit')} />
                  <Button variant="ghost" text="Close" onClick={handleOpenChange} />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default SalesConciergeWidget;
